"use client";

import { useEffect, useMemo } from "react";
import { captureProductClientEvent } from "@/lib/analytics/product-client-event";
import { ANALYTICS_EVENTS, type AnalyticsEvent } from "@/lib/analytics/events";
import { serializeError } from "@brev-io/common/src/utils/error-context";
import { decodeReactError } from "@/lib/react-error-decoder";
import { isProdEnvironment } from "@/lib/stageUtils";
import { EmptyState } from "@/components/ui/empty-state";
import {
  classifyStaleDeploy,
  type StaleDeployKind,
} from "./route-error-classification";
import { Note } from "@/components/ui/note";

const CHUNK_RELOAD_SESSION_KEY = "brev:chunk-load-reload-attempted";

// Each stale-deploy kind recovers the same way but is tracked under its own
// PostHog event so the recovery paths stay distinguishable in dashboards.
const STALE_DEPLOY_EVENT: Record<StaleDeployKind, AnalyticsEvent> = {
  chunk: ANALYTICS_EVENTS.CHUNK_LOAD_ERROR,
  "server-action-skew": ANALYTICS_EVENTS.SERVER_ACTION_VERSION_SKEW,
  "element-type-invalid": ANALYTICS_EVENTS.ELEMENT_TYPE_INVALID_ERROR,
};

/**
 * Reload the page once per session when a stale-bundle error is observed.
 * Guards against an infinite reload loop: if the new bundle still throws
 * the same error (e.g., the deploy is genuinely broken, or a real
 * undefined-component bug masquerades as a stale chunk), don't keep
 * reloading. The session-storage key persists across the reload and
 * blocks the second attempt, so the user falls through to the standard
 * error UI and can manually retry / report.
 */
function reloadOnceForStaleBundle(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) === "1") {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, "1");
  } catch {
    // sessionStorage can throw in private mode / when disabled. If we
    // can't track the attempt, skip the reload to avoid loops.
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * Whether a stale-bundle reload was already attempted earlier this session.
 * Read at capture time (before `reloadOnceForStaleBundle` sets the guard), so
 * `true` means this is a RECURRENCE after a reload — a strong signal the error
 * is a genuine undefined-component bug rather than transient deploy churn
 * (BRE-4415). Never throws.
 */
function hasAttemptedStaleReload(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export default function RouteErrorBoundary({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // chunk load, Server Action version skew, and React #130 ("element type is
  // invalid", where a rotated chunk resolves a component to `undefined`) are
  // all stale-bundle symptoms recovered the same way (reload once to pick up
  // the new deploy), so they share the "We've shipped an update" UI.
  const staleDeployKind = useMemo(() => classifyStaleDeploy(error), [error]);
  const staleDeployError = staleDeployKind !== null;

  useEffect(() => {
    const pathname = window.location.pathname;

    // BRE-4418: production React is minified, so a render crash logs only
    // "Minified React error #130" with no message. On the non-prod deployed
    // stages (staging + dev — where source maps are also on) decode the code
    // into its real text (e.g. "Element type is invalid … got: undefined") so
    // the console is actually useful without shipping a non-minified build.
    // Prod is untouched (gated off); decode returns null for uncurated codes,
    // so we never log a redundant line. Logged BEFORE the stale-deploy branch
    // so it survives the auto-reload (the console's "Preserve log" keeps it).
    const decodedReactError = decodeReactError(error);
    if (decodedReactError && !isProdEnvironment()) {
      console.error(`[RouteErrorBoundary] ${decodedReactError}`, {
        route: pathname,
        error_digest: error.digest,
      });
    }

    if (staleDeployKind) {
      // Capture FIRST so PostHog records the event even if the reload
      // happens before the next tick. Reload is best-effort; the capture
      // is the bug report.
      captureProductClientEvent(STALE_DEPLOY_EVENT[staleDeployKind], {
        route: pathname,
        error_digest: error.digest,
        // BRE-4415: `true` ⇒ this recurred after we already reloaded once this
        // session ⇒ the fresh bundle still throws ⇒ likely a genuine
        // undefined-component bug, not transient deploy churn. Lets dashboards
        // split self-healing blips from real regressions.
        reload_already_attempted: hasAttemptedStaleReload(),
        referrer:
          typeof document !== "undefined" ? document.referrer || undefined : undefined,
        // BRE-4418: ship the decoded message too, so #130-class events are
        // debuggable from the dashboard, not only the (non-prod) console.
        ...(decodedReactError ? { decoded_react_error: decodedReactError } : {}),
        ...serializeError(error),
      });
      reloadOnceForStaleBundle();
      return;
    }

    // Every other crash is a real bug the user is now staring at; without this
    // it reached PostHog only when something else happened to capture it.
    captureProductClientEvent(
      ANALYTICS_EVENTS.CLIENT_ERROR_DIAGNOSTIC,
      serializeError(error, {
        surface: "route-error-boundary",
        route: pathname,
        error_digest: error.digest,
        decoded_react_error: decodedReactError ?? undefined,
      }),
    );

    if (pathname.startsWith("/onboarding")) {
      captureProductClientEvent(ANALYTICS_EVENTS.ONBOARDING_ERROR_BOUNDARY_HIT, {
        // `route` is the original property name — keep it as-is so existing
        // PostHog insights / dashboards filtering by `properties.route` keep
        // working. Do not introduce a parallel `pathname` key.
        route: pathname,
        error_digest: error.digest,
        ...(decodedReactError ? { decoded_react_error: decodedReactError } : {}),
        ...serializeError(error),
      });
    }
  }, [error, staleDeployKind]);

  return (
    <EmptyState
      tone="error"
      title={staleDeployError ? "We've shipped an update" : "Something went wrong"}
      description={
        staleDeployError
          ? "Reload the page to pick up the latest version."
          : "We couldn't load this page. This might be a temporary issue."
      }
      onRetry={staleDeployError ? () => window.location.reload() : reset}
      retryLabel={staleDeployError ? "Reload" : "Try again"}
      className="flex-1 min-h-[50vh]"
    >
      {error.digest && (
        <Note>Error ID: {error.digest}</Note>
      )}
    </EmptyState>
  );
}
